import { useMemo } from "react";
import { format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/availability/DateField";
import { BLOCKED_DAY_CLASS, isUnavailableDay } from "@/components/availability/AvailabilityCalendar";
import {
  computeAvailableFrom,
  fromIso,
  stayEnd,
  validateStay,
  type AvailabilitySource,
  type StayIssue,
} from "@/lib/availability";
import { cn } from "@/lib/utils";

export interface StayValue {
  moveIn: Date | undefined;
  months: number;
}

// Airbnb-monthly style: pick a move-in date, dial the length of stay, and the
// move-out is computed. A day is disabled when a stay of the chosen length
// starting there would not fit (past, outside the window, over a blocked
// day); the database refuses the same stays (contact_requests_check_dates).
export const StayPicker = ({
  source,
  value,
  onChange,
  className,
}: {
  source: AvailabilitySource;
  value: StayValue;
  onChange: (v: StayValue) => void;
  className?: string;
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const minMonths = Math.max(1, source.minRentalMonths || 1);
  const months = Math.min(12, Math.max(minMonths, value.months || minMonths));
  const availableFrom = useMemo(() => computeAvailableFrom(source, today), [source, today]);
  const validation = value.moveIn ? validateStay(source, value.moveIn, months, today) : null;

  const issueText = (issue: StayIssue): string => {
    switch (issue) {
      case "past":
        return "That date has passed.";
      case "before_window":
        return `The owner's availability starts ${source.availableDate ? format(fromIso(source.availableDate), "MMM d, yyyy") : "later"}.`;
      case "after_window":
        return `The owner's availability ends ${source.availableUntil ? format(fromIso(source.availableUntil), "MMM d, yyyy") : "earlier"}. Try a shorter stay.`;
      case "min_stay":
        return `Minimum stay is ${minMonths} month${minMonths === 1 ? "" : "s"}.`;
      case "blocked":
        return `Blocked on ${validation?.firstBlocked ? format(fromIso(validation.firstBlocked), "MMM d") : "a day"} in that window. Try a shorter stay or a later move-in.`;
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* One column always (Oct 28): side by side, the date field had 59px left
          in the property page's sidebar at 1440px; and the stepper buttons grow to
          44px on phones (index.css), so the row never has a fixed height. */}
      <div className="grid grid-cols-1 gap-3" data-testid="stay-picker-grid">
        <div className="min-w-0">
          <p className="text-[13px] font-medium mb-1">Move-in date</p>
          <DateField
            mode="single"
            value={value.moveIn}
            onChange={(d) => onChange({ moveIn: d, months })}
            placeholder={availableFrom ? `From ${format(availableFrom, "MMM d")}` : "Pick a move-in date"}
            title="Move-in date"
            fromMonth={startOfMonth(today)}
            defaultMonth={value.moveIn ?? availableFrom ?? today}
            disabled={(d) => !validateStay(source, d, months, today).ok}
            modifiers={{ blocked: (d) => !isBefore(d, today) && isUnavailableDay(source, d) }}
            modifiersClassNames={{ blocked: BLOCKED_DAY_CLASS }}
            hint={`Min stay ${minMonths} month${minMonths === 1 ? "" : "s"}. Blocked days are red. Days where a ${months}-month stay would run into one can't be picked.`}
          />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-medium mb-1">Length of stay</p>
          <div className="inline-flex max-w-full items-center rounded-lg border bg-card min-h-10" data-testid="stay-months">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 min-h-0 rounded-l-lg rounded-r-none"
              aria-label="One month less"
              disabled={months <= minMonths}
              onClick={() => onChange({ moveIn: value.moveIn, months: Math.max(minMonths, months - 1) })}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="min-w-[5.5rem] px-2 text-center text-sm font-medium tabular-nums">
              {months} month{months === 1 ? "" : "s"}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 min-h-0 rounded-r-lg rounded-l-none"
              aria-label="One month more"
              disabled={months >= 12}
              onClick={() => onChange({ moveIn: value.moveIn, months: Math.min(12, months + 1) })}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      {value.moveIn && validation && (
        <p className={cn("text-[12px]", validation.ok ? "text-muted-foreground" : "text-destructive")}>
          {validation.ok
            ? `Move-out ${format(stayEnd(value.moveIn, months), "MMM d, yyyy")} · ${months} month${months === 1 ? "" : "s"}`
            : issueText(validation.issues[0])}
        </p>
      )}
    </div>
  );
};

export default StayPicker;
